import { OnboardingToolbar } from './OnboardingToolbar';
import { useFetchedPrompts } from '../../growegy-ai/library/hooks/useFetchedPrompts';
import React, { useEffect, useState } from 'react';
import { Chat, Prompt, VARIABLE_METADATAS } from '../../growegy-ai/types';
import { PromptPanel } from '../../growegy-ai/library/components/PromptPanel';
import { useFetchedProgramTypes } from '../../program/hooks/useFetchedProgramTypes';
import { AiStarsFilledIcon } from '../../assets/icons';
import { Messenger } from '../../growegy-ai/chat/components/Messenger';
import { usePromptClickedCallback } from '../../growegy-ai/chat/hooks/usePromptClickedCallback';
import { CHAT_TYPE_GENERAL } from '../../growegy-ai/dto';
import { createNewChat } from '../../growegy-ai/util';
import { BoxSpinner } from '../../commons/components/Spinner';
import { ChatRealmContextProvider } from '../../growegy-ai/chat/chatRealmContext';
import { nonBreakableSpace } from '../../util/utils';
import { useOnboardingWizardStepAnalyticsPage } from '../hooks/useOnboardingWizardStepAnalyticsPage';
import { ONBOARDING_STEP_PROMPT } from '../types';

const PromptStepInternal = (props: {
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onSkip: () => void;
  onHideHeader: () => void;
}) => {
  useOnboardingWizardStepAnalyticsPage(ONBOARDING_STEP_PROMPT);
  const { stepIndex, totalSteps, onNext, onSkip, onHideHeader } = props;
  const {
    query: { data: prompts },
    queryStatusComponent,
  } = useFetchedPrompts();
  const [onboardingPrompts, setOnboardingPrompts] = useState<Prompt[]>([]);
  useEffect(() => {
    if (onboardingPrompts.length > 0) return;
    if (!prompts) return;
    const onboardingPlaceholders = VARIABLE_METADATAS.filter((x) => x.includedInOnboarding).map(
      (x) => `[${x.name}]`
    );
    const candidatePrompts = prompts.prompts.filter((x) =>
      x.metadata.placeholders.every((ph) => onboardingPlaceholders.includes(ph.name))
    );
    const socialMediaPrompt =
      candidatePrompts.find((x) => x.title === 'Social Media Post Generation') ??
      candidatePrompts.find((x) => x.categories.includes('Social Media'));
    const strategyPrompt =
      candidatePrompts.find((x) => x.title === 'Campaign to Promote Product') ??
      candidatePrompts.find(
        (x) => x.categories.includes('Strategy') && x.id !== socialMediaPrompt?.id
      );
    const postingSchedulePrompt =
      candidatePrompts.find((x) => x.title === 'Social Media Posting Schedule') ??
      candidatePrompts.find(
        (x) =>
          x.categories.includes('Social Media') &&
          x.id !== socialMediaPrompt?.id &&
          x.id !== strategyPrompt?.id
      );
    const contentIdeasPrompt =
      candidatePrompts.find((x) => x.title === 'Content Ideas for Blog') ??
      candidatePrompts.find(
        (x) =>
          x.categories.includes('Content') &&
          x.id !== socialMediaPrompt?.id &&
          x.id !== strategyPrompt?.id &&
          x.id !== postingSchedulePrompt?.id
      );
    setOnboardingPrompts(
      [socialMediaPrompt, strategyPrompt, postingSchedulePrompt, contentIdeasPrompt].filter(
        (x) => x
      ) as Prompt[]
    );
  }, [onboardingPrompts, prompts]);
  const { data: programTypes } = useFetchedProgramTypes();
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [chat, setChat] = useState<Chat | null>(null);
  const onPromptClicked = usePromptClickedCallback(CHAT_TYPE_GENERAL, null, (chat) => {
    setChat(chat);
  });

  if (!prompts)
    return <div className="growegy-spinner__wrapper--big m-auto"> {queryStatusComponent}</div>;
  if (!programTypes) return null;

  return (
    <div className="onboarding-step__container">
      {!selectedPrompt && (
        <div>
          <span>
            <AiStarsFilledIcon /> Growegy AI saves you time by generating custom content for your
            business. Select one of these example prompts from our library to see how it works!
          </span>
        </div>
      )}
      {!selectedPrompt ? (
        <>
          <div className="onboarding-step__container overflow-auto onboarding-step__container--small-gap">
            {onboardingPrompts.map((x) => (
              <PromptPanel
                key={x.id}
                prompt={x}
                promptMark={null}
                onPromptClicked={async (prompt) => {
                  setSelectedPrompt(prompt);
                  await onPromptClicked(prompt);
                  onHideHeader();
                }}
                onPromptMark={Promise.resolve}
                existedTypes={programTypes.types}
                existedCategories={prompts.categories}
                hideHeader={true}
                className="prompt-panel__container--responsive"
              />
            ))}
          </div>
          <div className="billing__banner">
            💡 You can find more predefined prompts in Growegy{nonBreakableSpace}AI
            {nonBreakableSpace}→{nonBreakableSpace}Library.
          </div>
        </>
      ) : (
        <>
          {chat ? (
            <div
              className="onboarding-prompt__messenger-container messenger__slim messenger__slim--rounded-top"
              style={{ height: '75vh' }}
            >
              <Messenger
                chat={chat}
                onChatCreated={setChat}
                onNewChatClick={() => setChat(createNewChat(CHAT_TYPE_GENERAL, null))}
                hideIntro={true}
              />
            </div>
          ) : (
            <div className="flex-grow-1">
              <BoxSpinner />
            </div>
          )}
          <div className="billing__banner">
            🔎 Explore more options by adding chats, asking questions in Growegy{nonBreakableSpace}
            AI module, choosing prompts from the library, or generating a{nonBreakableSpace}
            personalized Business
            {nonBreakableSpace}Plan tailored to your needs.
          </div>
        </>
      )}
      <OnboardingToolbar
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        hideSkip={!!selectedPrompt}
        nextDisabled={!selectedPrompt}
        nextButtonText={selectedPrompt ? 'Continue to the app' : undefined}
        onNext={onNext}
        onSkip={onSkip}
      />
    </div>
  );
};

export const PromptStep = (props: {
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onSkip: () => void;
  onHideHeader: () => void;
}) => {
  return (
    <ChatRealmContextProvider chatType={CHAT_TYPE_GENERAL}>
      <PromptStepInternal {...props} />
    </ChatRealmContextProvider>
  );
};
